import styles from './TemplatesCategory.module.scss';

import { Button, Typography } from '@mui/material';
import Divider from '@mui/material/Divider';
import clsx from 'clsx';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDataProvider, useUser } from '@work4all/data';

import { Appointment } from '@work4all/models/lib/Classes/Appointment.entity';
import { Contract } from '@work4all/models/lib/Classes/Contract.entity';
import { Customer } from '@work4all/models/lib/Classes/Customer.entity';
import { Order } from '@work4all/models/lib/Classes/Order.entity';
import { Project } from '@work4all/models/lib/Classes/Project.entity';
import { Task } from '@work4all/models/lib/Classes/Task.entity';
import { Ticket } from '@work4all/models/lib/Classes/Ticket.entity';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { useTrackHistory } from '../../../hooks/use-time-track-history';
import { useTimeTrackingTemplates } from '../../../hooks/use-time-tracking-templates';
import { ProjectTimeMaskInit } from '../ProjectTimeMask';
import { categoryType } from '../TimeTemplatesWidget';

import { normalizeData, requestedData } from './TemplatesDataRequests';
import TemplateCard from './TenplateCard';

interface TemplatesCategoryProps {
  onTemplateSelect: (
    init: ProjectTimeMaskInit,
    createdFrom: Entities,
    createdFromId?: number
  ) => void;
  title: string;
  type: categoryType;
  mobileMode?: boolean;
}

const TemplatesCategory: React.FC<TemplatesCategoryProps> = ({
  title,
  type,
  onTemplateSelect,
  mobileMode = false,
}) => {
  const { lastTimeTemplates, saveTimeTemplate, categoriesSettings } =
    useTrackHistory();
  const user = useUser();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);

  const isRecentCategory = type === 'last';
  const recentItemsLength = lastTimeTemplates.length;
  const showCategoryBySettings = categoriesSettings[type].show;
  const countBySettings = categoriesSettings[type].count;

  const requestData = useMemo(() => {
    if (type === Entities.timeTracking)
      return {
        entity: Entities.timeTracking,
        data: [],
        filter: [],
        sort: [],
        skip: true,
      };

    return {
      entity: type === 'last' ? Entities.task : type,
      data: requestedData(user)[type === 'last' ? Entities.task : type].data,
      filter:
        requestedData(user)[type === 'last' ? Entities.task : type].filter,
      sort: requestedData(user)[type === 'last' ? Entities.task : type].sort,
    };
  }, [type, user]);

  const { data: responseData } = useDataProvider<
    Task | Order | Project | Appointment | Ticket
  >(requestData, requestData.skip);

  const data = normalizeData(
    responseData as Task[] | Contract[] | Project[] | Appointment[] | Ticket[],
    type
  );

  const createdFrom = type === 'last' ? Entities.task : type;

  const { list: timeTrackingTemplates } = useTimeTrackingTemplates();
  const isTimeTrackingTemplatesCategory = type === Entities.timeTracking;
  const timeTrackingTemplatesLength = timeTrackingTemplates.length;

  return showCategoryBySettings &&
    countBySettings > 0 &&
    ((!(isRecentCategory && !isTimeTrackingTemplatesCategory) &&
      data.length > 0) ||
      (isRecentCategory && recentItemsLength > 0) ||
      (isTimeTrackingTemplatesCategory && timeTrackingTemplatesLength > 0)) ? (
    <div>
      <Divider classes={{ root: styles.divider }} textAlign="left">
        {title}
      </Divider>
      <div
        className={clsx(styles.templatesGrid, { [styles.mobile]: mobileMode })}
      >
        {type === Entities.timeTracking
          ? timeTrackingTemplates
              .slice(0, open ? timeTrackingTemplates.length : countBySettings)
              .map((item, i) => {
                return (
                  <TemplateCard
                    type={type}
                    key={`${item.id}-${i}`}
                    title={
                      item.project
                        ? `${item.project.number} | ${item.project.name}`
                        : item.comment
                    }
                    subtitle1={item.customer?.name}
                    subtitle2={item.comment}
                    businessPartnerName={item.customer?.name}
                    onClick={() => {
                      onTemplateSelect(
                        {
                          project: item.project,
                          projectProcess: item.projectProcess,
                          customer: item.customer,
                          contract: item.contract,
                          position: item.position,
                          ticket: item.ticket,
                          activityArticle: item.activityArticle,
                          comment: item.comment,
                        },
                        createdFrom,
                        item.id
                      );
                    }}
                  />
                );
              })
          : type === 'last'
          ? lastTimeTemplates
              .slice(0, open ? lastTimeTemplates.length : countBySettings)
              .map((item, i) => (
                <TemplateCard
                  type={type}
                  key={`${item.title}-${item.subtitle1}-${i}`}
                  title={item.title}
                  subtitle1={item.subtitle1}
                  subtitle2={item.subtitle2}
                  subtitle3={item.subtitle3}
                  subtitle4={item.subtitle4}
                  onClick={() => {
                    onTemplateSelect(
                      {
                        project: item.toApply?.project,
                        projectProcess: item.toApply?.projectProcess,
                        customer: item.toApply?.businessPartner as Customer,
                        contract: item.toApply?.contract,
                        ticket: item.toApply?.ticket,
                      },
                      createdFrom
                    );
                  }}
                />
              ))
          : data
              .slice(0, open ? data.length : countBySettings)
              .map((item, i) => (
                <TemplateCard
                  type={type}
                  key={`${item.title}-${item.subtitle1}-${i}`}
                  title={item.title}
                  subtitle1={item.subtitle1}
                  subtitle2={item.subtitle2}
                  subtitle3={item.subtitle3}
                  subtitle4={item.subtitle4}
                  businessPartnerName={item?.businessPartnerName}
                  projectName={item?.projectName}
                  onClick={() => {
                    saveTimeTemplate(item);
                    onTemplateSelect(
                      {
                        project: item.toApply.project,
                        projectProcess: item.toApply.projectProcess,
                        customer: item.toApply.businessPartner,
                        contract: item.toApply.contract,
                        ticket: item.toApply.ticket,
                      },
                      createdFrom
                    );
                  }}
                />
              ))}
      </div>
      {(!isRecentCategory &&
        !isTimeTrackingTemplatesCategory &&
        data.length > countBySettings) ||
      (isRecentCategory && lastTimeTemplates.length > countBySettings) ||
      (isTimeTrackingTemplatesCategory &&
        timeTrackingTemplates.length > countBySettings) ? (
        <Button
          className={styles.moreButton}
          variant="text"
          onClick={() => setOpen(!open)}
        >
          {open
            ? 'weniger anzeigen'
            : `+${
                isRecentCategory
                  ? lastTimeTemplates.length - countBySettings
                  : isTimeTrackingTemplatesCategory
                  ? timeTrackingTemplates.length - countBySettings
                  : data.length - countBySettings
              } weitere`}
        </Button>
      ) : null}
    </div>
  ) : (
    <div>
      <Divider classes={{ root: styles.divider }} textAlign="left">
        {title}
      </Divider>
      <Typography variant="caption" color={'InactiveCaptionText'}>
        {t('COMMON.NO_DATA')}
      </Typography>
    </div>
  );
};

export default TemplatesCategory;
